import { Box, Stack, Typography } from "@mui/material";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { useEffect, useRef } from "react";
import {
  tileExtensionFromSlideFile,
  tileUrlPatternFromRoot,
} from "../titles/showTile/tileUrl";

function parseMarkerFeature(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed?.data || parsed || null;
  } catch {
    return null;
  }
}

function featureToStored(feature) {
  if (!feature?.geometry) return null;
  return JSON.stringify({ data: feature });
}

/**
 * Place a single exam-only marker on the slide (not library ShowTile pins).
 */
export default function ExamSimulatorQuestionMarkerMap({
  fileUrl,
  fileName,
  fileAlias,
  fileType,
  maxZoom = 4,
  markerLocation,
  onChange,
  disabled = false,
}) {
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const drawLayerRef = useRef(null);

  const tileExt = tileExtensionFromSlideFile({
    name: fileName,
    alias: fileAlias,
    type: fileType,
  });
  const tileURL = fileUrl
    ? tileUrlPatternFromRoot(String(fileUrl).replace(/\/+$/, ""), tileExt)
    : null;

  useEffect(() => {
    if (!tileURL) return undefined;

    const container = document.getElementById("exam-sim-marker-map");
    if (container?._leaflet_id) {
      container._leaflet_id = null;
    }

    const map = L.map("exam-sim-marker-map", {
      center: [0, 0],
      zoom: 2,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1,
    });
    mapRef.current = map;

    L.tileLayer(tileURL, {
      noWrap: true,
      maxZoom: Number(maxZoom) || 4,
      maxNativeZoom: Number(maxZoom) || 4,
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    drawLayerRef.current = drawnItems;
    map.addLayer(drawnItems);

    const feature = parseMarkerFeature(markerLocation);
    if (feature?.geometry) {
      const layer = L.geoJSON(feature);
      drawnItems.addLayer(layer);
      layerRef.current = layer;
    }

    const drawControl = disabled
      ? null
      : new L.Control.Draw({
          draw: {
            polygon: false,
            polyline: false,
            rectangle: false,
            circle: false,
            circlemarker: false,
            marker: {
              icon: L.divIcon({
                className: "exam-sim-marker-pin",
                html: '<div style="width:14px;height:14px;border-radius:50%;background:#1b8bff;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>',
                iconSize: [14, 14],
                iconAnchor: [7, 7],
              }),
            },
          },
          edit: {
            featureGroup: drawnItems,
            edit: true,
            remove: true,
          },
        });
    if (drawControl) {
      map.addControl(drawControl);
    }

    const onCreated = (e) => {
      drawnItems.clearLayers();
      drawnItems.addLayer(e.layer);
      layerRef.current = e.layer;
      const geo = e.layer.toGeoJSON();
      onChange?.(featureToStored(geo));
    };

    const onEdited = () => {
      let next = null;
      drawnItems.eachLayer((layer) => {
        next = featureToStored(layer.toGeoJSON());
      });
      onChange?.(next);
    };

    const onDeleted = () => {
      layerRef.current = null;
      onChange?.(null);
    };

    if (!disabled) {
      map.on(L.Draw.Event.CREATED, onCreated);
      map.on(L.Draw.Event.EDITED, onEdited);
      map.on(L.Draw.Event.DELETED, onDeleted);
    }

    setTimeout(() => map.invalidateSize(true), 100);
    setTimeout(() => map.invalidateSize(true), 400);

    return () => {
      if (!disabled) {
        map.off(L.Draw.Event.CREATED, onCreated);
        map.off(L.Draw.Event.EDITED, onEdited);
        map.off(L.Draw.Event.DELETED, onDeleted);
      }
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
      drawLayerRef.current = null;
    };
  }, [tileURL, maxZoom, disabled]);

  useEffect(() => {
    if (!drawLayerRef.current || !mapRef.current) return;
    const feature = parseMarkerFeature(markerLocation);
    if (feature?.geometry && layerRef.current) return;

    drawLayerRef.current.clearLayers();
    layerRef.current = null;
    if (feature?.geometry) {
      const layer = L.geoJSON(feature);
      drawLayerRef.current.addLayer(layer);
      layerRef.current = layer;
    }
  }, [markerLocation]);

  if (!fileUrl) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a slide to place the exam marker.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={700}>
        Exam marker (this page only)
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {disabled
          ? "Enable advanced edit to change the exam marker."
          : "Q4: use the marker tool on the slide to highlight the area the learner must describe. This does not change the slide's library annotations."}
      </Typography>
      <Box
        id="exam-sim-marker-map"
        sx={{
          height: 320,
          borderRadius: 2,
          overflow: "hidden",
          border: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: "background.paper",
        }}
      />
    </Stack>
  );
}
